/**
 * The formatters every listing page shares.
 *
 * Extracted during the gate on the Phase 3 port, which found `formatPrice`
 * copied four times, the Pacific date formatter twice, and the platform
 * title-caser inline in three more places. That is the same duplication this
 * whole port is organized against, on the presentation side: four copies of
 * "how do we render a price" drift, and the pages then disagree about the same
 * listing while every one of them looks fine.
 *
 * What deliberately did NOT move: `metaLine` and `summaryLine`. Those really do
 * differ per page — Browse's meta line omits the price when a deal line is
 * showing it, and Dismissed has no deals at all. They are similar-looking, not
 * the same function.
 *
 * `displayTitle` is a different story and this comment used to lump it in with
 * them, wrongly: it is byte-identical in three page libs and belongs here too.
 * It is left where it is only because moving it means a third round of test
 * relocation inside a review pass that has already grown, and it is recorded as
 * a follow-up rather than pretended away.
 */

/**
 * Rounds to the nearest whole dollar.
 *
 * `null` and `0` are different and must stay different: a free item is a price,
 * a listing whose price the scraper could not read is not. `NaN` is treated as
 * no price rather than rendering "$NaN".
 */
export function formatPrice(price: number | null): string | null {
  if (price === null || price === undefined || Number.isNaN(price)) return null;
  return `$${Math.round(price)}`;
}

/**
 * A date in PACIFIC, with an explicit timeZone.
 *
 * The explicit zone is the point: this is the project rule about never
 * slicing a UTC ISO string to get a calendar day. `"2026-03-22T05:00:00Z"`
 * is the 22nd in UTC and still the 21st here.
 *
 * `day: "2-digit"` zero-pads the day, e.g. "Mar 04, 2026" rather than
 * "Mar 4, 2026".
 *
 * Returns null rather than "Invalid Date" on anything unparseable: these
 * strings come off disk, and a card that fails to render is worse than a card
 * with no date.
 */
export function formatPacificDate(iso: string): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

/**
 * `depop` -> `Depop`.
 *
 * Deliberately NOT a full title-case pass: a naive title-case turns "USA"
 * into "Usa"; capitalizing only the first letter is wrong for
 * "big cartel" but wrong in a way that never mangles a name.
 */
export function platformLabel(platform: string): string {
  return platform ? platform.charAt(0).toUpperCase() + platform.slice(1) : "";
}
